import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AssetLinkingInput,
  AssetStatus,
  BusinessEntityType,
  GenerateTripsForShipmentsMutation,
  LoadType,
  ShipmentLocationType,
  ShipmentSortCriteria,
  Status,
  TripAssetTypes,
  TripForPlanningInput,
  PlanTripsForDriverMutation,
  useCombineShipmentsMutation,
  useAssignTripAssetsMutation,
  useChangeTripStartTimeMutation,
  useDeleteTripMutation,
  useGetAllDriverShiftsQuery,
  useGetAllMaintenanceTasksQuery,
  useGetAssignmentAssetsQuery,
  useGetBusinessEntityListQuery,
  useGetDriverListQuery,
  useGetOrganizationSettingsQuery,
  useGetShipmentListForPlanningQuery,
  usePlanTripsForDriverMutation,
  useSaveTripMutation,
  useUpdateShipmentMutation,
  useCheckShipmentsCombinabilityMutation,
  useGetDriverShiftAssignmentsQuery,
  useAddAssetLinkingMutation,
  useEditAssetLinkingMutation,
} from "../../../graphql/generated";
import PlanningTool, {
  AugmentedShipment,
  PlanningToolProps,
} from "./PlanningTool";
import {
  addSeconds,
  areIntervalsOverlapping,
  differenceInSeconds,
  endOfDay,
  isBefore,
  isEqual,
  startOfDay,
} from "date-fns";
import { Box } from "@mui/material";
import LoadingOverlay from "../../common/LoadingOverlay";
import { AssetFiltersValue } from "./AssetFilters";
// @ts-ignore
import parseAddress from "parse-address";
import { shipmentLocationAddressLabel } from "../../../utils/labels/shipmentLocationLabel";
import { ParsedAddressString } from "../../common/ExcelImporter";
import {
  groupBy,
  last,
  map,
  max,
  min,
  omit,
  sortBy,
  sum,
  toLower,
  uniq,
} from "lodash";
import { LoadFiltersValue } from "./LoadFilters";
import { mileage } from "../../../utils/conversion/distance";
import useDialog from "../../../utils/hooks/useDialog";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showToast } from "../../../redux/slices/alert/Alert.slice";
import { ShipmentListItem } from "../../shipment/ShipmentsList/ShipmentsList";
import { useLocation } from "react-router-dom";
import { useConfirm } from "material-ui-confirm";
import { validateAssetAssignment, PendingAssetChange } from "./assetValidation";
import { getInProgressTripDriveToNext } from "./inProgressTripDrive";
import { compareDriversByLastName } from "./driverSort";
import {
  buildErrorDialogContent,
  PlanningError,
} from "../utils/planningErrorUtils";
const states = require("us-state-converter");

type ReplannedTrip = PlanTripsForDriverMutation["planTripsForDriver"][number];

const replannedTripDistancesChanged = (
  originalTrip:
    | {
        distanceToStart?: number | null;
        distanceToEnd?: number | null;
        predictedLoadedDistance?: number | null;
      }
    | null
    | undefined,
  replannedTrip: ReplannedTrip,
) =>
  originalTrip?.distanceToStart !== replannedTrip.distanceToStart ||
  originalTrip?.distanceToEnd !== replannedTrip.distanceToEnd ||
  originalTrip?.predictedLoadedDistance !== replannedTrip.predictedLoadedDistance;

type PlanningToolContainerProps = Omit<
  PlanningToolProps,
  | "drivers"
  | "tractors"
  | "trailers"
  | "shipments"
  | "filteredShipments"
  | "assetFilters"
  | "loadFilters"
  | "driverShifts"
  | "maintenanceTasks"
  | "domiciles"
  | "onPeriodChange"
  | "onSubmit"
  | "onPlan"
  | "onRevert"
  | "onAssetSearchChange"
  | "onAssetFiltersChange"
  | "onLoadSearchChange"
  | "onLoadFiltersChange"
  | "onSortChange"
  | "onSortOrderDescChange"
  | "onShipmentsChange"
  | "onShipmentsCombine"
  | "onShipmentsUncombine"
  | "onRefresh"
  | "onRefreshAll"
  | "onSplitApplied"
  | "onFilteredShipmentsChange"
  | "onAutoPlanSubmit"
  | "isAutoPlanning"
  | "onAutoPlan"
  | "isAutoPlanModalOpen"
  | "onAssetsRefresh"
  | "onAssetLinkingChange"
>;

const PlanningToolContainer = (props: PlanningToolContainerProps) => {
  const { t } = useTranslation(["planning", "common"]);
  const {
    state,
  }: { state: { shipmentsToAutoPlan: ShipmentListItem[] } | undefined } =
    useLocation();
  const getDriverListQuery = useGetDriverListQuery(
    {
      where: {
        status: AssetStatus.Active,
      },
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const assignmentAssetsQuery = useGetAssignmentAssetsQuery(
    {},
    {
      refetchOnMount: false,
    },
  );
  const getAllDriverShiftsQuery = useGetAllDriverShiftsQuery(
    {},
    {
      refetchOnMount: false,
    },
  );
  const getAllMaintenanceTasksQuery = useGetAllMaintenanceTasksQuery(
    {},
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const getDriverShiftAssignmentsQuery = useGetDriverShiftAssignmentsQuery(
    {},
    {
      refetchOnMount: false,
    },
  );
  const domicilesQuery = useGetBusinessEntityListQuery(
    {
      businessEntityTypes: [BusinessEntityType.Domicile],
    },
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
  const combineShipmentsMutation = useCombineShipmentsMutation();
  const checkShipmentsCombinabilityMutation =
    useCheckShipmentsCombinabilityMutation();
  const saveTripMutation = useSaveTripMutation();
  const addAssetLinkingMutation = useAddAssetLinkingMutation();
  const editAssetLinkingMutation = useEditAssetLinkingMutation();

  const [currentPeriod, setCurrentPeriod] = useState({
    start: startOfDay(new Date()),
    end: endOfDay(new Date()),
  });

  // Create MongoDB filter for shipments that overlap with the current period
  // Use effectiveStartTime/effectiveEndTime (includes drive time) with fallback to firstPickupTime/lastDropoffTime
  const shipmentFilter = useMemo(() => {
    return {
      $or: [
        // Use effective times if available (includes drive time)
        {
          $and: [
            {
              "route.effectiveStartTime": {
                $exists: true,
                $ne: null,
                $lte: currentPeriod.end,
              },
            },
            {
              "route.effectiveEndTime": {
                $exists: true,
                $ne: null,
                $gte: currentPeriod.start,
              },
            },
          ],
        },
        // Fallback to regular times (for backward compatibility and shipments without effective times)
        {
          $and: [
            {
              "route.firstPickupTime": {
                $exists: true,
                $ne: null,
                $lte: currentPeriod.end,
              },
            },
            {
              "route.lastDropoffTime": {
                $exists: true,
                $ne: null,
                $gte: currentPeriod.start,
              },
            },
          ],
        },
      ],
    };
  }, [currentPeriod.start, currentPeriod.end]);

  const getShipmentListsQuery = useGetShipmentListForPlanningQuery({
    filter: shipmentFilter,
  });

  // Create refs to access queries in mutation callbacks
  const getShipmentListsQueryRef = useRef(getShipmentListsQuery);
  const getDriverListQueryRef = useRef(getDriverListQuery);

  useEffect(() => {
    getShipmentListsQueryRef.current = getShipmentListsQuery;
  }, [getShipmentListsQuery]);

  useEffect(() => {
    getDriverListQueryRef.current = getDriverListQuery;
  }, [getDriverListQuery]);

  const deleteTripMutation = useDeleteTripMutation({
    onSuccess: async () => {
      // Refetch shipment list and driver list after trip deletion
      // This ensures the planning tool updates immediately without manual refresh
      await Promise.all([
        getShipmentListsQueryRef.current.refetch(),
        getDriverListQueryRef.current.refetch(),
      ]);
    },
  });
  const assignTripAssetsMutation = useAssignTripAssetsMutation();
  const changeTripStartTimeMutation = useChangeTripStartTimeMutation();
  const updateShipmentMutation = useUpdateShipmentMutation();
  const planTripsForDriverMutation = usePlanTripsForDriverMutation();
  const confirm = useConfirm();

  const shipments = useMemo(
    () => getShipmentListsQuery.data?.shipments.data || [],
    [getShipmentListsQuery.data?.shipments.data],
  );

  const [localShipments, setLocalShipments] = useState(shipments);
  const [hasChanged, setHasChanged] = useState(false);
  const [hasPlanned, setHasPlanned] = useState(false);
  const localFilteredShipments = useRef(shipments);

  // Pending asset changes state for deferred save
  const [pendingAssetChanges, setPendingAssetChanges] = useState<
    Map<string, PendingAssetChange>
  >(new Map());

  // Helper function to update trips when driver assets change
  const updateTripsForDriverAssets = useCallback(
    (
      shipments: typeof localShipments,
      driverId: string,
      tractorId: string | null,
      trailerId: string | null,
      additionalTrailerIds: string[] | null,
    ): typeof localShipments => {
      return shipments.map((shipment) => {
        // Only update trips assigned to this driver
        if (shipment.trip?.driver !== driverId) {
          return shipment;
        }

        // Don't modify completed trips
        if (shipment.status === Status.Complete) {
          return shipment;
        }

        return {
          ...shipment,
          trip: {
            ...shipment.trip,
            tractor: tractorId,
            trailer: trailerId,
            additionalTrailers: additionalTrailerIds,
          },
        };
      });
    },
    [],
  );

  // Handler for asset linking changes (deferred save mode)
  const onAssetLinkingChange = useCallback(
    (
      driverId: string,
      assetLinkingInput: AssetLinkingInput,
      assetLinkingId?: string,
    ) => {
      // Determine if we should edit or create new asset linking
      // Only edit if the start and end dates match exactly
      let shouldEdit = false;

      if (assetLinkingId) {
        const allAssetLinkings =
          assignmentAssetsQuery.data?.assetLinkings.data || [];
        const existingAssetLinking = allAssetLinkings.find(
          (linking) => linking._id === assetLinkingId,
        );

        if (existingAssetLinking) {
          const existingStart = existingAssetLinking.startDate
            ? new Date(existingAssetLinking.startDate).getTime()
            : null;
          const existingEnd = existingAssetLinking.endDate
            ? new Date(existingAssetLinking.endDate).getTime()
            : null;
          const newStart = assetLinkingInput.startDate
            ? new Date(assetLinkingInput.startDate).getTime()
            : null;
          const newEnd = assetLinkingInput.endDate
            ? new Date(assetLinkingInput.endDate).getTime()
            : null;

          // Only edit if dates match exactly
          shouldEdit = existingStart === newStart && existingEnd === newEnd;
        }
      }

      // Store pending change in pendingAssetChanges map
      setPendingAssetChanges((prev) => {
        const newMap = new Map(prev);
        const pendingChange: PendingAssetChange = {
          driverId,
          assetLinkingInput,
          assetLinkingId: shouldEdit ? assetLinkingId : undefined, // Only include ID if we should edit
        };
        newMap.set(driverId, pendingChange);
        return newMap;
      });

      // Update all trips assigned to the driver with new assets
      setLocalShipments((currentShipments) =>
        updateTripsForDriverAssets(
          currentShipments,
          driverId,
          assetLinkingInput.tractor ?? null,
          assetLinkingInput.trailer ?? null,
          assetLinkingInput.additionalTrailers ?? null,
        ),
      );

      // Set hasChanged to true
      setHasChanged(true);
    },
    [
      updateTripsForDriverAssets,
      assignmentAssetsQuery.data?.assetLinkings.data,
    ],
  );

  useEffect(() => {
    if (hasChanged) {
      return;
    }
    setLocalShipments(shipments);
  }, [hasChanged, shipments]);

  const shipmentsWithStatesAndCities = localShipments.map((shipment) => {
    const parsedOrigin: ParsedAddressString | null = parseAddress.parseLocation(
      shipmentLocationAddressLabel(shipment.route?.locations?.[0]),
    );
    const parsedDestination: ParsedAddressString | null =
      parseAddress.parseLocation(
        shipmentLocationAddressLabel(
          shipment.route?.locations?.[shipment.route.locations.length - 1],
        ),
      );

    return {
      ...shipment,
      originState: parsedOrigin?.state || "",
      originCity:
        parsedOrigin?.city ||
        shipmentLocationAddressLabel(shipment.route?.locations?.[0]),
      destinationState: parsedDestination?.state || "",
      destinationCity:
        parsedDestination?.city ||
        shipmentLocationAddressLabel(
          shipment.route?.locations?.[shipment.route.locations.length - 1],
        ),
    };
  });

  const localGroupedShipments = useMemo(() => {
    return map(
      groupBy(
        shipmentsWithStatesAndCities,
        (shipment) => shipment.tripId || shipment._id,
      ),
      (shipmentGroup, tripId) => ({
        ...shipmentGroup[0],
        otherShipmentsInTrip: (shipmentGroup as AugmentedShipment[]).slice(1),
      }),
    );
  }, [shipmentsWithStatesAndCities]);

  const shipmentsWithEarliestLatestPickup = localGroupedShipments.map(
    (shipment) => {
      const firstPickupLocation =
        shipment.trip?.shipmentLocations[0] || shipment.route?.locations?.[0];

      if (!firstPickupLocation) {
        throw new Error(
          "No pickup location found for shipment " + shipment._id,
        );
      }

      const earliestPickupByTimeWindow = min(
        firstPickupLocation?.timeWindows.map((tw) => tw.fromDate),
      );
      const latestPickupByTimeWindow = max(
        firstPickupLocation?.timeWindows.map((tw) => tw.toDate),
      );

      const locationsWithTimeWindows = (
        shipment.trip?.shipmentLocations ||
        shipment.route?.locations ||
        []
      ).filter(
        (location) =>
          location !== firstPickupLocation && location.timeWindows.length > 0,
      );

      const earliestPickupToGetToAllLocations = max(
        locationsWithTimeWindows.map((locationToVisit) => {
          const locationVisitMinTime = new Date(
            min(locationToVisit.timeWindows.map((tw) => tw.fromDate)),
          );

          const duration = differenceInSeconds(
            new Date(locationToVisit.arrivalTime),
            new Date(firstPickupLocation.arrivalTime),
          );

          const earliestPickup = addSeconds(locationVisitMinTime, -duration);

          return earliestPickup;
        }),
      );

      const latestPickupToGetToAllLocations = min(
        locationsWithTimeWindows.map((locationToVisit) => {
          const locationVisitMaxTime = new Date(
            max(locationToVisit.timeWindows.map((tw) => tw.toDate)),
          );

          const duration = differenceInSeconds(
            new Date(locationToVisit.arrivalTime),
            new Date(firstPickupLocation.arrivalTime),
          );

          const latestPickup = addSeconds(locationVisitMaxTime, -duration);

          return latestPickup;
        }),
      );

      const earliestPickup = max([
        earliestPickupByTimeWindow,
        earliestPickupToGetToAllLocations,
      ]);
      const latestPickup = min([
        latestPickupByTimeWindow,
        latestPickupToGetToAllLocations,
      ]);

      const lastDropoffLocation = last(
        shipment.trip?.shipmentLocations || shipment.route?.locations || [],
      );

      const earliestDropoff = min(
        lastDropoffLocation?.timeWindows.map((tw) => tw.fromDate),
      );
      const latestDropoff = max(
        lastDropoffLocation?.timeWindows.map((tw) => tw.toDate),
      );

      return {
        ...shipment,
        earliestPickup,
        latestPickup,
        earliestDropoff,
        latestDropoff,
      };
    },
  );

  const shipmentsWithEarliestLatestPickupById = useMemo(
    () => new Map(shipmentsWithEarliestLatestPickup.map((s) => [s._id, s])),
    [shipmentsWithEarliestLatestPickup],
  );

  const orgId = localStorage.getItem("keycloak::orgId");
  const PLANNING_FILTERS_KEY = `${orgId}::planning-tool-filters`;

  const [assetSearch, setAssetSearch] = useState("");
  const [assetFilters, setAssetFilters] = useState<AssetFiltersValue>(() => {
    try {
      const saved = localStorage.getItem(`${PLANNING_FILTERS_KEY}::asset`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      trailerTypes: null,
      hosRange: null,
      hosStatus: null,
      domicileIds: null,
    };
  });

  const [loadSearch, setLoadSearch] = useState("");
  const [loadFilters, setLoadFilters] = useState<LoadFiltersValue>(() => {
    try {
      const saved = localStorage.getItem(`${PLANNING_FILTERS_KEY}::load`);
      if (saved) return JSON.parse(saved);
    } catch {}
    return {
      trailerTypes: null,
      originStates: null,
      destinationStates: null,
      originCities: null,
      destinationCities: null,
      customer: null,
      shipper: null,
      receiver: null,
      minRevenue: null,
      minRevenuePerMile: null,
    };
  });

  const [sortCriteria, setSortCriteria] = useState<ShipmentSortCriteria | null>(
    ShipmentSortCriteria.PickupDate,
  );
  const [sortOrderDesc, setSortOrderDesc] = useState(false);

  useEffect(() => {
    localStorage.setItem(
      `${PLANNING_FILTERS_KEY}::asset`,
      JSON.stringify(assetFilters),
    );
  }, [assetFilters, PLANNING_FILTERS_KEY]);

  useEffect(() => {
    localStorage.setItem(
      `${PLANNING_FILTERS_KEY}::load`,
      JSON.stringify(loadFilters),
    );
  }, [loadFilters, PLANNING_FILTERS_KEY]);

  let filteredShipments = sortBy(
    shipmentsWithEarliestLatestPickup
      .filter((shipment) => {
        if (loadSearch) {
          const loadSearchText = `${
            shipment.shipmentNumber
          } ${shipment.referenceNumbers?.join(" ")} ${shipment.originState} ${
            shipment.originCity
          } ${shipment.destinationState} ${shipment.destinationCity} ${
            shipment.customer.name
          } ${shipment.shipmentLocations.map(
            (location) =>
              `${location.name} ${location.addressLabel} ${location.shippedGoods
                ?.map((good) => good.label)
                .join(" ")}`,
          )}`;
          return loadSearchText
            .toLowerCase()
            .includes(loadSearch.toLowerCase());
        }
        return true;
      })
      .filter((shipment) => {
        if (!loadFilters.statuses?.length) {
          return true;
        }
        return loadFilters.statuses.includes(shipment.status);
      })
      .filter((shipment) => {
        if (!loadFilters.trailerTypes?.length) {
          return true;
        }
        if (!shipment.trailerType) {
          return false;
        }
        return loadFilters.trailerTypes.includes(shipment.trailerType);
      })
      .filter((shipment) => {
        if (loadFilters.originStates?.length) {
          const shipmentOriginState = shipment.originState;
          if (!shipmentOriginState) {
            return false;
          }
          return loadFilters.originStates
            .map(states.abbr)
            .includes(shipmentOriginState);
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.destinationStates?.length) {
          const shipmentDestinationState = shipment.destinationState;
          if (!shipmentDestinationState) {
            return false;
          }
          return loadFilters.destinationStates
            .map(states.abbr)
            .includes(shipmentDestinationState);
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.originCities?.length) {
          const shipmentOriginCity = shipment.originCity;
          if (!shipmentOriginCity) {
            return false;
          }
          return loadFilters.originCities
            .map(toLower)
            .includes(shipmentOriginCity.toLowerCase());
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.destinationCities?.length) {
          const shipmentDestinationCity = shipment.destinationCity;
          if (!shipmentDestinationCity) {
            return false;
          }
          return loadFilters.destinationCities
            .map(toLower)
            .includes(shipmentDestinationCity.toLowerCase());
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.customer) {
          return shipment.customer._id === loadFilters.customer;
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.shipper) {
          return shipment.shipmentLocations.some(
            (shipmentLocation) =>
              shipmentLocation.locationType === ShipmentLocationType.Pickup &&
              shipmentLocation.shipper?._id === loadFilters.shipper,
          );
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.receiver) {
          return shipment.shipmentLocations.some(
            (shipmentLocation) =>
              shipmentLocation.locationType === ShipmentLocationType.DropOff &&
              shipmentLocation.receiver?._id === loadFilters.receiver,
          );
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.minRevenue) {
          return (
            sum(shipment.charges.map((charge) => charge.total)) >=
            loadFilters.minRevenue
          );
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.minRevenuePerMile) {
          return (
            sum(shipment.charges.map((charge) => charge.total)) /
              mileage(shipment.route?.routeDistance || 0) >=
            loadFilters.minRevenuePerMile
          );
        }
        return true;
      })
      .filter((shipment) => {
        if (loadFilters.firstPickupTime || loadFilters.lastDropoffTime) {
          const filterInterval = {
            start: loadFilters.firstPickupTime
              ? startOfDay(new Date(loadFilters.firstPickupTime))
              : undefined,
            end: loadFilters.lastDropoffTime
              ? endOfDay(new Date(loadFilters.lastDropoffTime))
              : undefined,
          };

          const shipmentStart = new Date(
            shipment.route?.firstPickupTime || shipment.trip?.firstPickupTime,
          );
          const shipmentEnd = new Date(
            shipment.route?.lastDropoffTime || shipment.trip?.lastDropoffTime,
          );

          return (
            (filterInterval.start
              ? shipmentStart >= filterInterval.start
              : true) ||
            (filterInterval.end ? shipmentEnd <= filterInterval.end : true)
          );
        }
        return true;
      }),
    (shipment) => {
      if (sortCriteria === ShipmentSortCriteria.PickupDate) {
        return (
          shipment.trip?.firstPickupTime || shipment.route?.firstPickupTime
        );
      }
      if (sortCriteria === ShipmentSortCriteria.DropoffDate) {
        return (
          shipment.trip?.lastDropoffTime || shipment.route?.lastDropoffTime
        );
      }
      if (sortCriteria === ShipmentSortCriteria.PickupDistance) {
        return shipment.route?.routeDistance;
      }
      if (sortCriteria === ShipmentSortCriteria.ShipmentNumber) {
        // shipmentNumber is a number, but it's stored as a string
        return Number(shipment.shipmentNumber);
      }
      if (sortCriteria === ShipmentSortCriteria.Revenue) {
        return -sum(shipment.charges.map((charge) => charge.total));
      }
      if (sortCriteria === ShipmentSortCriteria.RevenuePerMile) {
        return (
          -sum(shipment.charges.map((charge) => charge.total)) /
          mileage(shipment.route?.routeDistance || 0)
        );
      }
      // Default to sorting by pickup date
      return shipment.trip?.firstPickupTime || shipment.route?.firstPickupTime;
    },
  );

  filteredShipments = sortOrderDesc
    ? filteredShipments.reverse()
    : filteredShipments;

  const assetLinkings = useMemo(
    () => assignmentAssetsQuery.data?.assetLinkings.data || [],
    [assignmentAssetsQuery.data?.assetLinkings.data],
  );

  const shiftAssignments = useMemo(
    () =>
      getDriverShiftAssignmentsQuery.data?.driverShiftAssignments.data || [],
    [getDriverShiftAssignmentsQuery.data?.driverShiftAssignments.data],
  );

  const driversWithAssociatedAssets = useMemo(
    () =>
      (assignmentAssetsQuery.data?.drivers.data || []).map((driver) => {
        // Check if there's a pending asset change for this driver
        const pendingChange = pendingAssetChanges.get(driver._id);

        // Check if pending change applies to current period
        if (pendingChange) {
          const pendingStart = pendingChange.assetLinkingInput.startDate
            ? new Date(pendingChange.assetLinkingInput.startDate)
            : new Date(0);
          const pendingEnd = pendingChange.assetLinkingInput.endDate
            ? new Date(pendingChange.assetLinkingInput.endDate)
            : new Date(MAX_TIMESTAMP);

          const pendingAppliesToCurrentPeriod = areIntervalsOverlapping(
            currentPeriod,
            {
              start: pendingStart,
              end: pendingEnd,
            },
          );

          if (pendingAppliesToCurrentPeriod) {
            // Use pending asset changes instead of database values
            const tractors = assignmentAssetsQuery.data?.tractors.data || [];
            const trailers = assignmentAssetsQuery.data?.trailers.data || [];

            const linkedTractor = pendingChange.assetLinkingInput.tractor
              ? tractors.find(
                  (t) => t._id === pendingChange.assetLinkingInput.tractor,
                )
              : null;
            const linkedTrailer = pendingChange.assetLinkingInput.trailer
              ? trailers.find(
                  (t) => t._id === pendingChange.assetLinkingInput.trailer,
                )
              : null;
            const linkedPupTrailer = (
              pendingChange.assetLinkingInput.additionalTrailers || []
            )
              .map((id) => trailers.find((t) => t._id === id))
              .filter((t): t is NonNullable<typeof t> => t !== undefined);

            const driverShiftAssignment = sortBy(
              shiftAssignments,
              (assignment) =>
                assignment.startDate
                  ? -new Date(assignment.startDate).getTime()
                  : Infinity,
            ).find(
              (assignment) =>
                assignment.driverId === driver._id &&
                areIntervalsOverlapping(currentPeriod, {
                  start: new Date(assignment.startDate || 0),
                  end: new Date(assignment.endDate || MAX_TIMESTAMP),
                }),
            );

            // Create a lightweight assetLinking object from pending change
            // so the modal can reopen with correct initial values
            const pendingAssetLinking = {
              _id: pendingChange.assetLinkingId || "", // Use existing ID if editing, empty string if new
              driver: driver,
              tractor: linkedTractor,
              trailer: linkedTrailer,
              additionalTrailers: linkedPupTrailer,
              startDate: pendingChange.assetLinkingInput.startDate,
              endDate: pendingChange.assetLinkingInput.endDate,
            };

            return {
              ...driver,
              associatedTractor: linkedTractor || null,
              associatedTrailer: linkedTrailer || null,
              associatedAdditionalTrailers: linkedPupTrailer || [],
              assetLinking: pendingAssetLinking, // Provide pending values for modal reopening
              driverShiftAssignment,
            };
          }
        }

        // No pending changes for current period, use database values
        const assetLinking = sortBy(assetLinkings, (linking) =>
          linking.startDate ? -new Date(linking.startDate).getTime() : Infinity,
        ).find(
          (assetLinking) =>
            assetLinking.driver?._id === driver._id &&
            // This should never happen due to validation but in case the interval is wrong
            // we don't want a crash
            isBefore(
              new Date(assetLinking.startDate || 0),
              new Date(assetLinking.endDate || MAX_TIMESTAMP),
            ) &&
            areIntervalsOverlapping(currentPeriod, {
              start: new Date(assetLinking.startDate || 0),
              end: new Date(assetLinking.endDate || MAX_TIMESTAMP),
            }),
        );
        const linkedTractor = assetLinking?.tractor;
        const linkedTrailer = assetLinking?.trailer;
        const linkedPupTrailer = assetLinking?.additionalTrailers;

        const driverShiftAssignment = sortBy(shiftAssignments, (assignment) =>
          assignment.startDate
            ? -new Date(assignment.startDate).getTime()
            : Infinity,
        ).find(
          (assignment) =>
            assignment.driverId === driver._id &&
            areIntervalsOverlapping(currentPeriod, {
              start: new Date(assignment.startDate || 0),
              end: new Date(assignment.endDate || MAX_TIMESTAMP),
            }),
        );

        return {
          ...driver,
          associatedTractor: linkedTractor ? linkedTractor : null,
          associatedTrailer: linkedTrailer ? linkedTrailer : null,
          associatedAdditionalTrailers: linkedPupTrailer
            ? linkedPupTrailer
            : [],
          assetLinking,
          driverShiftAssignment,
        };
      }),
    [
      assetLinkings,
      shiftAssignments,
      assignmentAssetsQuery.data?.drivers.data,
      assignmentAssetsQuery.data?.tractors.data,
      assignmentAssetsQuery.data?.trailers.data,
      currentPeriod,
      pendingAssetChanges,
    ],
  );

  const filteredDrivers = useMemo(
    () =>
      driversWithAssociatedAssets
        .filter((driver) => {
          if (assetSearch) {
            const driverSearchText = `${driver.firstname} ${driver.lastname} ${driver.associatedTractor?.serialNumber} ${driver.associatedTrailer?.serialNumber}`;
            return driverSearchText
              .toLowerCase()
              .includes(assetSearch.toLowerCase());
          }
          return true;
        })
        .filter((driver) => {
          if (!assetFilters.trailerTypes?.length) {
            return true;
          }
          if (!driver.associatedTrailer?.type) {
            return false;
          }
          return assetFilters.trailerTypes.includes(
            driver.associatedTrailer.type,
          );
        })
        .filter((driver) => {
          if (assetFilters.domicileIds?.length) {
            return assetFilters.domicileIds.includes(driver.domicileId);
          }
          return true;
        }),
    [
      assetFilters.domicileIds,
      assetFilters.trailerTypes,
      assetSearch,
      driversWithAssociatedAssets,
    ],
  );

  // The board shows drivers alphabetically by last name.
  const sortedDrivers = useMemo(
    () => [...filteredDrivers].sort(compareDriversByLastName),
    [filteredDrivers],
  );

  const loading =
    getDriverListQuery.isLoading ||
    getShipmentListsQuery.isLoading ||
    getShipmentListsQuery.isFetching ||
    assignmentAssetsQuery.isLoading ||
    assignmentAssetsQuery.isFetching ||
    combineShipmentsMutation.isLoading ||
    deleteTripMutation.isLoading ||
    assignTripAssetsMutation.isLoading ||
    changeTripStartTimeMutation.isLoading ||
    saveTripMutation.isLoading ||
    deleteTripMutation.isLoading ||
    updateShipmentMutation.isLoading ||
    getAllDriverShiftsQuery.isLoading ||
    getAllMaintenanceTasksQuery.isLoading ||
    domicilesQuery.isLoading ||
    planTripsForDriverMutation.isLoading;

  const { showDialog } = useDialog();

  const planDriver = async (
    driverId: string,
    currentShipments: AugmentedShipment[],
  ): Promise<{
    success: boolean;
    error?: string;
    trips?: ReplannedTrip[];
  }> => {
    const driver = driversWithAssociatedAssets.find(
      (driver) => driver._id === driverId,
    );
    if (!driver) {
      return { success: false, error: "Driver not found" };
    }

    const driverShipments = currentShipments.filter(
      (shipment) =>
        shipment.trip?.driver === driverId &&
        shipment.status !== Status.Complete &&
        shipment.status !== Status.InProgress,
    );

    let earliestChange: Date | undefined;

    const updateEarliestChange = (date: Date) => {
      earliestChange = earliestChange
        ? min([earliestChange, date]) || date
        : date;
    };

    // Check shipments still assigned to this driver for any changes
    driverShipments.forEach((localShipment) => {
      const originalShipment = shipments.find(
        (shipment) => shipment._id === localShipment._id,
      );

      if (!originalShipment) {
        return;
      }

      const tripHasChanged =
        originalShipment.trip?.driver !== localShipment.trip?.driver ||
        originalShipment.trip?.tractor !== localShipment.trip?.tractor ||
        originalShipment.trip?.trailer !== localShipment.trip?.trailer ||
        originalShipment.trip?.firstPickupTime !==
          localShipment.trip?.firstPickupTime ||
        // Detect when a shipment was combined onto a different trip — the trip
        // assets may be unchanged but the route needs to be re-planned.
        originalShipment.tripId !== localShipment.tripId;

      if (tripHasChanged && localShipment.trip?.firstPickupTime) {
        updateEarliestChange(new Date(localShipment.trip.firstPickupTime));
      }
    });

    // Also check for shipments that were originally assigned to this driver
    // but have since been moved away — their removal affects this driver's schedule.
    // Note: `shipments` is the original server state (pre-edit); `currentShipments`
    // (via shipmentsWithEarliestLatestPickupById) is the local unsaved state.
    shipments.forEach((originalShipment) => {
      if (originalShipment.trip?.driver !== driverId) {
        return;
      }
      const localShipment = shipmentsWithEarliestLatestPickupById.get(
        originalShipment._id,
      );
      const wasRemoved =
        !localShipment || localShipment.trip?.driver !== driverId;
      if (wasRemoved && originalShipment.trip?.firstPickupTime) {
        updateEarliestChange(new Date(originalShipment.trip.firstPickupTime));
      }
    });

    if (!earliestChange) {
      return { success: true };
    }

    const shipmentsFromEarliestChange = driverShipments.filter(
      (shipment) =>
        new Date(shipment.trip?.firstPickupTime) >= startOfDay(earliestChange!),
    );

    const sortedShipmentsFromEarliestChange = sortBy(
      shipmentsFromEarliestChange,
      (shipment) =>
        shipment.trip?.firstPickupTime || shipment.route?.firstPickupTime,
    );

    if (shipmentsFromEarliestChange.length <= 0) {
      return { success: true };
    }

    const tripsForPlanning: TripForPlanningInput[] =
      sortedShipmentsFromEarliestChange.map((shipment) => {
        return {
          shipmentIds: [
            shipment._id,
            ...(shipment.otherShipmentsInTrip || []).map((s) => s._id),
          ],
          firstPickupTime: shipment.trip?.firstPickupTime,
          lastDropoffTime: shipment.trip?.lastDropoffTime,
          shipmentLocations: (shipment.trip?.shipmentLocations || []).map(
            (location) => ({
              _id: location._id,
              locationType: location.locationType,
              arrivalTime: location.arrivalTime,
              shipmentIds:
                location.shipmentIds ||
                (location.shipmentId ? [location.shipmentId] : []),
              shipmentLocationIds: location.shipmentLocationIds || [],
              shipmentId: location.shipmentId,
            }),
          ),
          canMoveForward: true,
          canMoveBackward: false,
        };
      });

    try {
      const { planTripsForDriver: trips } =
        await planTripsForDriverMutation.mutateAsync({
          driverId,
          trips: tripsForPlanning,
          tractorId: driver.associatedTractor?._id,
          trailerId: driver.associatedTrailer?._id,
          additionalTrailerIds: driver.associatedAdditionalTrailers?.map(
            (trailer) => trailer._id,
          ),
        });

      const tripsWithPersistedIds = trips.map((trip) => {
        const localShipment = driverShipments.find((shipment) =>
          trip.shipments.some((plannedShipment) =>
            plannedShipment._id === shipment._id,
          ),
        );

        return {
          ...trip,
          _id: localShipment?.trip?._id || trip._id,
        };
      });

      // Point the in-progress trip's drive at the next trip, not the domicile
      const inProgressDrive = getInProgressTripDriveToNext(
        currentShipments,
        driverId,
        tripsWithPersistedIds,
      );

      setLocalShipments((localShipments) =>
        localShipments.map((localShipment) => {
          if (
            inProgressDrive &&
            localShipment.trip?._id === inProgressDrive.tripId
          ) {
            return {
              ...localShipment,
              trip: {
                ...localShipment.trip,
                etaToNextTrip: inProgressDrive.etaToNextTrip,
              },
            };
          }
          if (driverShipments.some((s) => s._id === localShipment._id)) {
            const tripForShipment = tripsWithPersistedIds.find((trip) =>
              trip.shipments.map((s) => s._id).includes(localShipment._id),
            );
            if (!tripForShipment) {
              return localShipment;
            }
            return {
              ...localShipment,
              trip: {
                ...tripForShipment,
                _id: localShipment.trip?._id || "",
                tripNumber:
                  tripForShipment.tripNumber ||
                  localShipment.trip?.tripNumber ||
                  "NEW",
                driver: tripForShipment.driver?._id,
                tractor: tripForShipment.tractor?._id,
                trailer: tripForShipment.trailer?._id,
                additionalTrailers: tripForShipment.additionalTrailers?.map(
                  (trailer) => trailer._id,
                ),
              },
            };
          }
          return localShipment;
        }),
      );
      return { success: true, trips: tripsWithPersistedIds };
    } catch (error) {
      return {
        success: false,
        error: (error as Error).message || "Unknown error",
      };
    }
  };

  const orgSettingsQuery = useGetOrganizationSettingsQuery();

  const orgSettings = orgSettingsQuery.data?.organizationSettings;

  const dispatch = useDispatch();

  const handleAutoPlanSubmit = async (
    result: GenerateTripsForShipmentsMutation["generateTripsForShipments"],
  ) => {
    const trips = result.trips;
    const unplannedShipments = result.unplannedShipments;
    const unplannedShipmentsIds = unplannedShipments.map(
      (shipment) => shipment._id,
    );

    setLocalShipments((localShipments) =>
      localShipments.map((localShipment) => {
        if (unplannedShipmentsIds.includes(localShipment._id)) {
          return {
            ...localShipment,
            status: Status.Unplanned,
          };
        }
        const tripForShipment = trips.find((trip) =>
          trip.shipments.map((s) => s._id).includes(localShipment._id),
        );
        if (!tripForShipment) {
          return localShipment;
        }
        return {
          ...localShipment,
          tripId: tripForShipment._id || localShipment.trip?._id || null,
          trip: {
            ...tripForShipment,
            _id: tripForShipment._id || localShipment.trip?._id || "",
            tripNumber:
              tripForShipment.tripNumber ||
              localShipment.trip?.tripNumber ||
              "NEW",
            driver: tripForShipment.driver?._id,
            tractor: tripForShipment.tractor?._id,
            trailer: tripForShipment.trailer?._id,
          },
          status: tripForShipment ? Status.Assigned : localShipment.status,
        };
      }),
    );

    dispatch(
      showToast({
        message: t(
          "autoPlanModal.autoPlanSuccess",
          "Orders have been planned successfully. You can save to apply changes.",
        ),
      }),
    );
    setHasChanged(true);
    setHasPlanned(true);
  };

  const onShipmentsChange: PlanningToolProps["onShipmentsChange"] = useCallback(
    (augmentedShipments) => {
      setHasChanged(true);
      setHasPlanned(false);
      // Explode grouped shipments back to single shipments
      // for compatibility
      const updatedLocalShipments = augmentedShipments.flatMap(
        (augmentedShipment) => [
          augmentedShipment,
          ...(augmentedShipment.otherShipmentsInTrip || []).map((s) => ({
            ...s,
            tripId: augmentedShipment.tripId,
            trip: augmentedShipment.trip,
          })),
        ],
      );
      setLocalShipments(updatedLocalShipments);
    },
    [],
  );

  const onShipmentsUncombine: PlanningToolProps["onShipmentsUncombine"] =
    useCallback(
      (shipmentsToUncombine) => {
        const previewTrip = shipmentsToUncombine[0]?.trip;
        const tripIdKey = shipmentsToUncombine[0]?.tripId ?? previewTrip?._id;
        if (!previewTrip || previewTrip.tripNumber !== "NEW" || !tripIdKey) {
          return;
        }

        const idsToSeparate = new Set(
          shipmentsToUncombine.map((s) => String(s._id)),
        );

        setLocalShipments((prev) => {
          const isOnLocalNewTrip = (localShipment: typeof prev[0]) =>
            localShipment.trip?.tripNumber === "NEW" &&
            (localShipment.tripId === tripIdKey ||
              String(localShipment.trip?._id) === String(tripIdKey));

          const remainingIds = prev
            .filter(
              (s) => isOnLocalNewTrip(s) && !idsToSeparate.has(String(s._id)),
            )
            .map((s) => String(s._id));

          return prev.map((localShipment) => {
            if (!isOnLocalNewTrip(localShipment)) {
              return localShipment;
            }

            if (idsToSeparate.has(String(localShipment._id))) {
              const original = shipments.find(
                (s) => s._id === localShipment._id,
              );
              return {
                ...localShipment,
                tripId: original?.tripId ?? null,
                trip: original?.trip ?? null,
              };
            }

            const trip = localShipment.trip;
            if (!trip || remainingIds.length === 0) {
              return localShipment;
            }

            return {
              ...localShipment,
              trip: {
                ...trip,
                shipmentLocations: trip.shipmentLocations?.filter((sl) =>
                  sl.shipmentIds?.some((id) =>
                    remainingIds.includes(String(id)),
                  ),
                ),
              },
            };
          });
        });
        setHasChanged(true);
      },
      [shipments],
    );

  // Validation callback for asset assignment conflicts
  const handleValidateAssetAssignment = useCallback(
    (assetLinkingInput: AssetLinkingInput, currentDriverId: string) => {
      const existingAssetLinkings =
        assignmentAssetsQuery.data?.assetLinkings.data || [];
      const drivers = assignmentAssetsQuery.data?.drivers.data || [];

      return validateAssetAssignment(
        assetLinkingInput,
        currentDriverId,
        existingAssetLinkings,
        pendingAssetChanges,
        drivers,
      );
    },
    [assignmentAssetsQuery.data, pendingAssetChanges],
  );

  return (
    <Box
      sx={{
        height: "100%",
      }}
    >
      <LoadingOverlay loading={loading} />
      <PlanningTool
        drivers={sortedDrivers}
        tractors={assignmentAssetsQuery.data?.tractors.data || []}
        trailers={assignmentAssetsQuery.data?.trailers.data || []}
        shipments={shipmentsWithEarliestLatestPickup}
        filteredShipments={filteredShipments}
        assetFilters={assetFilters}
        loadFilters={loadFilters}
        driverShifts={getAllDriverShiftsQuery.data?.driverShifts.data || []}
        maintenanceTasks={
          getAllMaintenanceTasksQuery.data?.maintenanceTasks.data.filter(
            (task) => task.required,
          ) || []
        }
        domiciles={domicilesQuery.data?.businessEntities.data || []}
        canSave={hasChanged}
        canPlan={hasChanged}
        canRevert={hasChanged}
        onPeriodChange={(period) => {
          setCurrentPeriod(period);
        }}
        onAssetSearchChange={(search) => {
          setAssetSearch(search);
        }}
        onAssetFiltersChange={(filters) => {
          setAssetFilters(filters);
        }}
        onLoadSearchChange={(search) => {
          setLoadSearch(search);
        }}
        onLoadFiltersChange={(filters) => {
          setLoadFilters(filters);
        }}
        onSortChange={(sortCriteria) => {
          setSortCriteria(sortCriteria);
        }}
        onSortOrderDescChange={(sortDesc) => {
          setSortOrderDesc(sortDesc);
        }}
        onShipmentsChange={onShipmentsChange}
        onShipmentsUncombine={onShipmentsUncombine}
        onShipmentsCombine={async (shipmentsToCombine) => {
          try {
            if (
              uniq(shipmentsToCombine.map((shipment) => shipment.trailerType))
                .length > 1
            ) {
              throw new Error(
                t(
                  "error.combineOrdersWithDifferentTrailersError",
                  "Cannot combine orders with different trailer types",
                ),
              );
            }
            if (
              shipmentsToCombine.some(
                (shipment) => shipment.loadType === LoadType.FullTruckLoad,
              )
            ) {
              throw new Error(
                t(
                  "error.combineFullTruckerLoadError",
                  "Cannot combine full truck load orders",
                ),
              );
            }

            const hasShipmentInProgress =
              shipmentsToCombine.length > 1 &&
              shipmentsToCombine.some(
                (shipment) => shipment.status === Status.InProgress,
              );

            if (
              hasShipmentInProgress &&
              !orgSettings?.dispatching?.canConsolidateInProgressShipments
            ) {
              if (showDialog) {
                showDialog({
                  title: t("orders:alert.inProgress", "In progress shipments"),
                  description: t(
                    "orders:alert.inProgressDesc",
                    "You can't consolidate in progress shipments",
                  ),
                  type: "primary",
                  actions: [
                    {
                      type: "primary",
                      title: t("common:OK", "OK"),
                    },
                  ],
                });
              }
              return;
            }

            const areAllOverlapping = shipmentsToCombine.every((shipment1) => {
              return shipmentsToCombine.some((shipment2) => {
                if (shipment1 === shipment2) {
                  return false;
                }
                const minTimeWindowDate1 = min(
                  shipment1.shipmentLocations.flatMap((loc) =>
                    loc.timeWindows.map((tw) => tw.fromDate),
                  ),
                );
                const maxTimeWindowDate1 = max(
                  shipment1.shipmentLocations.flatMap((loc) =>
                    loc.timeWindows.map((tw) => tw.toDate),
                  ),
                );
                const minTimeWindowDate2 = min(
                  shipment2.shipmentLocations.flatMap((loc) =>
                    loc.timeWindows.map((tw) => tw.fromDate),
                  ),
                );
                const maxTimeWindowDate2 = max(
                  shipment2.shipmentLocations.flatMap((loc) =>
                    loc.timeWindows.map((tw) => tw.toDate),
                  ),
                );

                return (
                  (minTimeWindowDate1 <= maxTimeWindowDate2 &&
                    minTimeWindowDate1 >= minTimeWindowDate2) ||
                  (maxTimeWindowDate1 >= minTimeWindowDate2 &&
                    maxTimeWindowDate1 <= maxTimeWindowDate2) ||
                  (minTimeWindowDate2 <= maxTimeWindowDate1 &&
                    minTimeWindowDate2 >= minTimeWindowDate1) ||
                  (maxTimeWindowDate2 >= minTimeWindowDate1 &&
                    maxTimeWindowDate2 <= maxTimeWindowDate1)
                );
              });
            });

            if (!areAllOverlapping) {
              throw new Error(
                t(
                  "error.combineOrdersWithoutOverlappingTimeWindowsError",
                  "Cannot combine orders without overlapping time windows",
                ),
              );
            }
            await checkShipmentsCombinabilityMutation.mutateAsync({
              generateTripData: {
                shipments: shipmentsToCombine.map((shipment) => shipment._id),
                trailer: shipmentsToCombine[0].trip?.trailer,
                tractor: shipmentsToCombine[0].trip?.tractor,
                driver: shipmentsToCombine[0].trip?.driver,
                additionalTrailers:
                  shipmentsToCombine[0].trip?.additionalTrailers,
                startTime: shipmentsToCombine[0].trip?.firstPickupTime,
              },
            });

            const combineResult = await combineShipmentsMutation.mutateAsync({
              generateTripData: {
                shipments: shipmentsToCombine.map((shipment) => shipment._id),
                tractor: shipmentsToCombine[0].trip?.tractor,
                trailer: shipmentsToCombine[0].trip?.trailer,
                driver: shipmentsToCombine[0].trip?.driver,
                additionalTrailers:
                  shipmentsToCombine[0].trip?.additionalTrailers,
                startTime: shipmentsToCombine[0].trip?.firstPickupTime,
              },
            });

            const previewedTrip = combineResult.combineShipments;

            setLocalShipments((localShipments) =>
              localShipments.map((localShipment) => {
                if (
                  shipmentsToCombine
                    .map((s) => s._id)
                    .includes(localShipment._id)
                ) {
                  return {
                    ...localShipment,
                    tripId: previewedTrip._id,
                    trip: {
                      ...previewedTrip,
                      tripNumber: "NEW",
                      driver: previewedTrip.driver?._id,
                      tractor: previewedTrip.tractor?._id,
                      trailer: previewedTrip.trailer?._id,
                      additionalTrailers: previewedTrip.additionalTrailers?.map(
                        (t) => t._id,
                      ),
                    },
                  };
                }
                return localShipment;
              }),
            );
            setHasChanged(true);
          } catch (error) {
            showDialog({
              title: t("common:error.title", "Error"),
              type: "error",
              description: `${t(
                "error.combineOrdersError",
                "An error occurred while combining orders",
              )}. ${
                (error as Error).message ||
                t("common:error.unknownError", "Unknown error")
              }`,
            });
            console.error(error);
          }
        }}
        onRefresh={async (shipmentIds) => {
          const shipmentListResponse = await getShipmentListsQuery.refetch();

          const newShipments = shipmentListResponse.data?.shipments.data || [];

          setLocalShipments(
            (localShipments) =>
              localShipments
                .map((localShipment) => {
                  if (shipmentIds.includes(localShipment._id)) {
                    return (
                      newShipments.find(
                        (newShipment) => newShipment._id === localShipment._id,
                      ) || null
                    );
                  }
                  return localShipment;
                })
                .filter(Boolean) as (
                | typeof newShipments[0]
                | typeof localShipments[0]
              )[],
          );
        }}
        onSplitApplied={async (parentId, childIds) => {
          const shipmentListResponse = await getShipmentListsQuery.refetch();
          const newShipments = shipmentListResponse.data?.shipments.data || [];
          const childIdSet = new Set(childIds);
          const children = newShipments.filter((shipment) =>
            childIdSet.has(shipment._id),
          );
          const affectedTripIds = new Set(
            children
              .map((shipment) => shipment.tripId)
              .filter((tripId): tripId is string => !!tripId),
          );

          setLocalShipments((currentLocalShipments) => {
            const withoutParent = currentLocalShipments.filter(
              (shipment) => shipment._id !== parentId,
            );

            // Sibling shipments on the same trip still embed the pre-split
            // location IDs; refresh those so Plan can match Vroom shipments.
            const withSyncedTrips = withoutParent.map((shipment) => {
              if (
                !shipment.tripId ||
                !affectedTripIds.has(shipment.tripId) ||
                !shipment.trip
              ) {
                return shipment;
              }
              const serverSibling = children.find(
                (child) =>
                  child.tripId === shipment.tripId &&
                  child.trip?.shipmentLocations,
              );
              if (!serverSibling?.trip) {
                return shipment;
              }
              return {
                ...shipment,
                trip: {
                  ...shipment.trip,
                  shipmentLocations: serverSibling.trip.shipmentLocations,
                },
              };
            });

            const localIds = new Set(withSyncedTrips.map((s) => s._id));
            const additions = children.filter(
              (child) => !localIds.has(child._id),
            );
            return withSyncedTrips.concat(additions);
          });
        }}
        onRefreshAll={async () => {
          await getShipmentListsQuery.refetch();
        }}
        onAssetsRefresh={async () => {
          await Promise.all([
            assignmentAssetsQuery.refetch(),
            getDriverShiftAssignmentsQuery.refetch(),
            getAllDriverShiftsQuery.refetch(),
          ]);
        }}
        onAssetLinkingChange={onAssetLinkingChange}
        validateAssetAssignment={handleValidateAssetAssignment}
        onPlan={async () => {
          const originalShipmentsById = new Map(
            shipments.map((s) => [s._id, s]),
          );

          const driversWithChanges = Array.from(
            localGroupedShipments.reduce((acc, localShipment) => {
              const originalShipment = originalShipmentsById.get(
                localShipment._id,
              );
              if (!originalShipment) {
                return acc;
              }
              const hasChanged =
                originalShipment.trip?.driver !== localShipment.trip?.driver ||
                originalShipment.trip?.tractor !==
                  localShipment.trip?.tractor ||
                originalShipment.trip?.trailer !==
                  localShipment.trip?.trailer ||
                originalShipment.trip?.firstPickupTime !==
                  localShipment.trip?.firstPickupTime ||
                originalShipment.tripId !== localShipment.tripId;

              if (hasChanged) {
                if (localShipment.trip?.driver) {
                  acc.add(localShipment.trip.driver);
                }
                if (originalShipment.trip?.driver) {
                  acc.add(originalShipment.trip.driver);
                }
              }
              return acc;
            }, new Set<string>()),
          );

          const driverNameMap = new Map(
            driversWithAssociatedAssets.map((driver) => [
              driver._id,
              `${driver.firstname} ${driver.lastname}`,
            ]),
          );

          const errors: PlanningError[] = [];

          for (const driverId of driversWithChanges) {
            const driverName = driverNameMap.get(driverId) || driverId;
            const result = await planDriver(
              driverId,
              shipmentsWithEarliestLatestPickup,
            );

            if (!result.success) {
              errors.push({
                driverId,
                driverName,
                message: result.error || "Unknown error",
              });
            }
          }

          if (errors.length > 0) {
            showDialog({
              title: t("common:error.title", "Cannot Plan Orders"),
              type: "error",
              description: buildErrorDialogContent(errors),
            });

            throw new Error(`Planning failed for ${errors.length} driver(s)`);
          }
          setHasPlanned(true);
        }}
        onRevert={async () => {
          try {
            await confirm({
              title: "Revert Changes",
              description: "Are you sure you want to revert all changes?",
            });

            // Clear pending asset changes
            setPendingAssetChanges(new Map());
            setLocalShipments(shipments);
            setHasChanged(false);
            setHasPlanned(false);
          } catch (error) {
            if (error) {
              console.error(error);
              showDialog({
                title: "Error",
                description: "An error occurred while reverting changes.",
              });
            }
          }
        }}
        onSubmit={async () => {
          const earliestChangesByDriver: Record<string, Date> = {};
          try {
            // Validate by planning all drivers with changes before saving
            const originalShipmentsById = new Map(
              shipments.map((s) => [s._id, s]),
            );
            const driversWithChanges = Array.from(
              localGroupedShipments.reduce((acc, localShipment) => {
                const originalShipment = originalShipmentsById.get(
                  localShipment._id,
                );
                if (!originalShipment) {
                  return acc;
                }
                const hasChanged =
                  originalShipment.trip?.driver !==
                    localShipment.trip?.driver ||
                  originalShipment.trip?.tractor !==
                    localShipment.trip?.tractor ||
                  originalShipment.trip?.trailer !==
                    localShipment.trip?.trailer ||
                  originalShipment.trip?.firstPickupTime !==
                    localShipment.trip?.firstPickupTime ||
                  originalShipment.tripId !== localShipment.tripId;
                if (hasChanged) {
                  if (localShipment.trip?.driver) {
                    acc.add(localShipment.trip.driver);
                  }
                  if (originalShipment.trip?.driver) {
                    acc.add(originalShipment.trip.driver);
                  }
                }
                return acc;
              }, new Set<string>()),
            );

            // Create driver name map for errors
            const driverNameMap = new Map(
              driversWithAssociatedAssets.map((driver) => [
                driver._id,
                `${driver.firstname} ${driver.lastname}`,
              ]),
            );

            // Collect errors from all drivers instead of failing fast
            const errors: PlanningError[] = [];
            const replannedTripsByTripId = new Map<string, ReplannedTrip>();

            for (const driverId of driversWithChanges) {
              const driverName = driverNameMap.get(driverId) || driverId;
              const result = await planDriver(
                driverId,
                shipmentsWithEarliestLatestPickup,
              );

              if (!result.success) {
                errors.push({
                  driverId,
                  driverName,
                  message: result.error || "Unknown error",
                });
                continue;
              }

              result.trips?.forEach((trip) => {
                if (trip._id) {
                  replannedTripsByTripId.set(trip._id, trip);
                }
              });
            }

            // If there are any errors, show them all at once
            if (errors.length > 0) {
              showDialog({
                title: t("common:error.title", "Cannot Plan Orders"),
                type: "error",
                description: buildErrorDialogContent(errors),
              });
              // Re-throw to prevent saving
              throw new Error(`Planning failed for ${errors.length} driver(s)`);
            }

            // 1. Save pending asset linkings first
            for (const [, change] of Array.from(
              pendingAssetChanges.entries(),
            )) {
              if (change.assetLinkingId) {
                await editAssetLinkingMutation.mutateAsync({
                  id: change.assetLinkingId,
                  editAssetLinkingData: change.assetLinkingInput,
                });
              } else {
                await addAssetLinkingMutation.mutateAsync({
                  newAssetLinkingData: change.assetLinkingInput,
                });
              }
            }

            // Clear pending asset changes immediately after saving them
            // This prevents duplicate linkings if a later trip mutation fails and user retries
            setPendingAssetChanges(new Map());

            // Refetch asset linkings to update local state with newly saved data
            await assignmentAssetsQuery.refetch();

            // 2. Save trips (existing logic)
            await Promise.all(
              localGroupedShipments.map(async (localShipment) => {
                const originalShipment = shipments.find(
                  (shipment) => shipment._id === localShipment._id,
                );

                if (!originalShipment) {
                  return;
                }

                const tripHasChanged =
                  originalShipment.trip?.driver !==
                    localShipment.trip?.driver ||
                  originalShipment.trip?.tractor !==
                    localShipment.trip?.tractor ||
                  originalShipment.trip?.trailer !==
                    localShipment.trip?.trailer ||
                  originalShipment.trip?.firstPickupTime !==
                    localShipment.trip?.firstPickupTime;

                if (tripHasChanged && localShipment.trip?.driver) {
                  earliestChangesByDriver[localShipment.trip.driver] =
                    min([
                      earliestChangesByDriver[localShipment.trip.driver],
                      new Date(localShipment.trip.firstPickupTime),
                    ]) || new Date(localShipment.trip.firstPickupTime);
                }

                if (
                  originalShipment.trip &&
                  localShipment.trip &&
                  localShipment.trip.tripNumber !== "NEW"
                ) {
                  // Don't modify completed trips
                  if (originalShipment.status === Status.Complete) {
                    return;
                  }

                  // Helper to compare additionalTrailers arrays
                  const additionalTrailersChanged = () => {
                    const originalTrailers = (
                      originalShipment.trip?.additionalTrailers || []
                    )
                      .slice()
                      .sort()
                      .join(",");
                    const localTrailers = (
                      localShipment.trip?.additionalTrailers || []
                    )
                      .slice()
                      .sort()
                      .join(",");
                    return originalTrailers !== localTrailers;
                  };

                  // If the driver, tractor, trailer, or additionalTrailers has changed, assign the new assets to the trip
                  if (
                    originalShipment.trip.driver !==
                      localShipment.trip?.driver ||
                    originalShipment.trip.tractor !==
                      localShipment.trip?.tractor ||
                    originalShipment.trip.trailer !==
                      localShipment.trip?.trailer ||
                    additionalTrailersChanged()
                  ) {
                    await assignTripAssetsMutation.mutateAsync({
                      assignmentData: [
                        {
                          tripId: localShipment.trip._id,
                          assetType: TripAssetTypes.Driver,
                          assetId: localShipment.trip.driver,
                          restartTrip:
                            originalShipment.status === Status.InProgress,
                        },
                        {
                          tripId: localShipment.trip._id,
                          assetType: TripAssetTypes.Tractor,
                          assetId: localShipment.trip.tractor,
                          restartTrip:
                            originalShipment.status === Status.InProgress,
                        },
                        {
                          tripId: localShipment.trip._id,
                          assetType: TripAssetTypes.Trailer,
                          assetId: localShipment.trip.trailer,
                          restartTrip:
                            originalShipment.status === Status.InProgress,
                        },
                        {
                          tripId: localShipment.trip._id,
                          assetType: TripAssetTypes.AdditionalTrailers,
                          assetId:
                            localShipment.trip.additionalTrailers?.join(",") ??
                            null,
                          restartTrip:
                            originalShipment.status === Status.InProgress,
                        },
                      ],
                      ignoreDriverShifts: true,
                    });
                  }

                  const replannedTrip = localShipment.trip?._id
                    ? replannedTripsByTripId.get(localShipment.trip._id)
                    : undefined;

                  const pickupTimeHasChanged =
                    originalShipment.trip?.firstPickupTime &&
                    localShipment.trip?.firstPickupTime &&
                    !isEqual(
                      new Date(originalShipment.trip.firstPickupTime),
                      new Date(localShipment.trip.firstPickupTime),
                    );
                  const etasHaveChanged =
                    originalShipment.trip.etaFromPreviousTrip !==
                      localShipment.trip.etaFromPreviousTrip ||
                    originalShipment.trip.etaToNextTrip !==
                      localShipment.trip.etaToNextTrip;
                  const distancesHaveChanged = replannedTrip
                    ? replannedTripDistancesChanged(
                        originalShipment.trip,
                        replannedTrip,
                      )
                    : false;
                  if (
                    pickupTimeHasChanged ||
                    etasHaveChanged ||
                    distancesHaveChanged
                  ) {
                    await changeTripStartTimeMutation.mutateAsync({
                      changeTripStartTimeData: {
                        tripId: localShipment.trip._id,
                        startTime: localShipment.trip.firstPickupTime,
                        etaFromPreviousTrip:
                          localShipment.trip.etaFromPreviousTrip,
                        etaToNextTrip: localShipment.trip.etaToNextTrip,
                        distanceToStart:
                          replannedTrip?.distanceToStart ??
                          localShipment.trip.distanceToStart,
                        distanceToEnd:
                          replannedTrip?.distanceToEnd ??
                          localShipment.trip.distanceToEnd,
                        predictedLoadedDistance:
                          replannedTrip?.predictedLoadedDistance ??
                          localShipment.trip.predictedLoadedDistance,
                        violations: localShipment.trip.violations,
                      },
                    });
                  }
                } else if (
                  (!originalShipment?.trip ||
                    localShipment.trip?.tripNumber === "NEW") &&
                  localShipment.trip
                ) {
                  await saveTripMutation.mutateAsync({
                    saveTripData: {
                      ...omit(localShipment.trip, ["_id", "tripNumber"]),
                      shipments: [
                        localShipment._id,
                        ...localShipment.otherShipmentsInTrip.map((s) => s._id),
                      ],
                      shipmentLocations:
                        localShipment.trip.shipmentLocations.map((sl) => ({
                          ...sl,
                          shippedGoods: sl.shippedGoods || [],
                          receivedGoods: sl.receivedGoods || [],
                          // @ts-ignore
                          shipper: sl.shipper?._id,
                          // @ts-ignore
                          receiver: sl.receiver?._id,
                        })),
                      driver: localShipment.trip.driver,
                      tractor: localShipment.trip.tractor,
                      trailer: localShipment.trip.trailer,
                      additionalTrailers: localShipment.trip.additionalTrailers,
                      etaFromPreviousTrip:
                        localShipment.trip.etaFromPreviousTrip,
                      etaToNextTrip: localShipment.trip.etaToNextTrip,
                      violations: localShipment.trip.violations,
                    },
                  });
                } else if (originalShipment.trip && !localShipment.trip) {
                  // If the driver has been removed, unassign the driver from the trip
                  await deleteTripMutation.mutateAsync({
                    id: originalShipment.trip._id,
                  });
                } else if (originalShipment.status !== localShipment.status) {
                  await updateShipmentMutation.mutateAsync({
                    id: localShipment._id,
                    editShipmentData: {
                      status: localShipment.status,
                    },
                  });
                }
              }),
            );

            await Promise.all([
              getShipmentListsQuery.refetch(),
              getDriverListQuery.refetch(),
              assignmentAssetsQuery.refetch(),
            ]);
            setHasChanged(false);
          } catch (error) {
            // Only show dialog if it's not a planning aggregation error (already shown)
            const errorMessage = (error as Error)?.message || "";
            if (!errorMessage.includes("Planning failed")) {
              showDialog({
                title: t("common:error.title", "Error"),
                type: "error",
                description: `${t(
                  "error.saveAssignmentsError",
                  "An error occurred while saving assignments",
                )}. ${
                  (error as Error).message ||
                  t("common:error.unknownError", "Unknown error")
                }`,
              });
            }
            console.error(error);
          }
        }}
        onFilteredShipmentsChange={(filteredShipments) => {
          localFilteredShipments.current = filteredShipments;
        }}
        onAutoPlanSubmit={handleAutoPlanSubmit}
        isAutoPlanModalOpen={state?.shipmentsToAutoPlan.length === 0}
        {...props}
        showDriveTimes={hasPlanned}
      />
    </Box>
  );
};

export default PlanningToolContainer;

const MAX_TIMESTAMP = 8640000000000000;
